/**
 * Size chart behaviour.
 *
 * No drawer is rendered inline — the markup is fetched on first use. Charts are
 * shared between products, so the cache is keyed by metaobject handle: a collection
 * of one product family costs a single request however many cards are opened.
 */

/** metaobject handle -> Promise<HTMLElement> (rejects if the chart cannot load) */
const sizeChartCache = new Map();

/**
 * Handles whose last attempt failed. A failed entry leaves the cache so a click can
 * retry, but prefetch must not: pointerover bubbles, so one mouse sweep across the
 * trigger would fire a request per descendant.
 */
const sizeChartFailed = new Set();

const SIZE_CHART_CTA = '.js-size-chart-cta';

const SIZE_CHART_ANCHORED = 'size-chart-drawer--above-selector';
const SIZE_CHART_MIN_SPACE = 200;

let anchored = null;

/**
 * Below 990px product-form.js moves a card's size panel to <body> as a bottom sheet.
 * A guide opened from there must not cover the sizes: per Figma it takes only the
 * space left above the sheet.
 *
 * @param {HTMLElement} cta - the clicked trigger
 * @returns {HTMLElement|null} the sheet to sit above, null for every other trigger
 */
function sheetSizePanel(cta) {
  const panel = cta.closest('.variant-selector');
  // On desktop the panel is anchored inside its own card, and the guide stays centred.
  return panel && panel.parentElement === document.body ? panel : null;
}

/**
 * Anchors a drawer above the size sheet, or restores the centred popup. Charts are
 * cached and reopened from any trigger, so the second case has to clear what an
 * earlier open set.
 *
 * @param {HTMLElement} drawer - the mounted <drawer-simple>
 * @param {HTMLElement|null} panel - the sheet to sit above, if any
 */
function applyAnchor(drawer, panel) {
  const occupied = panel ? window.innerHeight - panel.getBoundingClientRect().top : 0;

  if (!panel || window.innerHeight - occupied < SIZE_CHART_MIN_SPACE) {
    drawer.classList.remove(SIZE_CHART_ANCHORED);
    drawer.style.removeProperty('--size-chart-sheet-height');
    return;
  }

  drawer.style.setProperty('--size-chart-sheet-height', `${Math.round(occupied)}px`);
  drawer.classList.add(SIZE_CHART_ANCHORED);
}

window.addEventListener(
  'resize',
  () => {
    if (!anchored) return;
    const { drawer, panel } = anchored;
    if (!drawer.classList.contains('opened') || !panel.isConnected) {
      anchored = null;
      return;
    }
    applyAnchor(drawer, panel);
  },
  { passive: true },
);

/**
 * Puts a fetched drawer in the DOM and returns the live element.
 * @param {string} html - the ?view=size-chart response
 * @param {string} handle - size_chart metaobject handle
 * @returns {HTMLElement} the mounted <drawer-simple>
 */
function mountSizeChart(html, handle) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const drawer = parsed.querySelector('drawer-simple');
  if (!drawer) throw new Error('response contained no drawer');

  // Several charts can be in the DOM at once, so each needs its own id.
  drawer.id = `size-chart-drawer--${handle}`;
  drawer.classList.add('size-chart-drawer--elevated');

  // Appending upgrades the element and runs connectedCallback, which binds its own
  // close handlers.
  document.body.append(drawer);
  return document.getElementById(drawer.id);
}

/**
 * Fetches and caches one chart. Concurrent callers share a single request.
 * @param {string} handle - size_chart metaobject handle
 * @param {string} url - the product's ?view=size-chart URL
 * @returns {Promise<HTMLElement>} rejects if the chart cannot be loaded
 */
function loadSizeChart(handle, url) {
  if (!sizeChartCache.has(handle)) {
    const request = fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`size chart responded with ${response.status}`);
        return response.text();
      })
      .then((html) => mountSizeChart(html, handle));

    // Drop a failed entry so a later click retries.
    request.catch(() => {
      sizeChartCache.delete(handle);
      sizeChartFailed.add(handle);
    });
    sizeChartCache.set(handle, request);
  }

  return sizeChartCache.get(handle);
}

/**
 * Switches the visible unit table. A custom element rather than a delegated
 * listener, so a fetched drawer needs no global wiring.
 */
class SizeChartSwitcher extends HTMLElement {
  connectedCallback() {
    // Moving the drawer re-runs this; bind once.
    if (this.bound) return;
    this.bound = true;

    // change bubbles, so one listener covers every input inside.
    this.addEventListener('change', (event) => {
      const input = event.target.closest('.js-size-chart__switcher-input');
      if (input) this.select(input);
    });
  }

  select(input) {
    this.querySelectorAll('.js-size-chart__table').forEach((table) => {
      table.classList.toggle('hidden', table.dataset.chart !== input.value);
    });
    this.querySelectorAll('.js-size-chart__switcher-label').forEach((label) => {
      label.classList.toggle('filled', label.getAttribute('for') === input.id);
    });
  }
}

customElements.define('size-chart-switcher', SizeChartSwitcher);

// Opening is delegated: the triggers are scattered across cards and the PDP, and no
// single element owns them.

// Warm the cache on intent, so the first open does not visibly wait on a request.
const prefetchSizeChart = (event) => {
  const cta = event.target.closest?.(SIZE_CHART_CTA);
  if (!cta?.dataset.sizeChart || !cta.dataset.sizeChartUrl) return;
  if (sizeChartFailed.has(cta.dataset.sizeChart)) return;

  // Speculative — a failure here is the click handler's problem.
  loadSizeChart(cta.dataset.sizeChart, cta.dataset.sizeChartUrl).catch(() => {});
};

// pointerover only where hovering means intent: on touch it fires once per tap,
// just before the click that fetches anyway.
if (window.matchMedia('(hover: hover)').matches) {
  document.addEventListener('pointerover', prefetchSizeChart, { passive: true });
}
document.addEventListener('focusin', prefetchSizeChart, { passive: true });

document.addEventListener('click', (event) => {
  const drawer = anchored?.drawer;
  if (!drawer?.classList.contains('opened')) return;
  if (drawer.contains(event.target)) return;
  drawer.close();
});

document.addEventListener('click', async (event) => {
  const cta = event.target.closest(SIZE_CHART_CTA);
  if (!cta) return;

  const { sizeChart: handle, sizeChartUrl: url } = cta.dataset;
  // Nothing to fetch — if the trigger is a link, let the browser follow it.
  if (!handle || !url) return;

  event.preventDefault();
  // A deliberate click always gets a fresh attempt.
  sizeChartFailed.delete(handle);
  cta.removeAttribute('aria-disabled');
  cta.setAttribute('aria-busy', 'true');

  try {
    const drawer = await loadSizeChart(handle, url);
    const panel = sheetSizePanel(cta);
    anchored = panel ? { drawer, panel } : null;
    applyAnchor(drawer, panel);
    // the anchored layout may give up modality — otherwise FocusLayer inerts the panel and the sizes stop responding.
    const uncovered = drawer.classList.contains(SIZE_CHART_ANCHORED);
    drawer.open(cta, uncovered ? { modal: false, contain: false, lockScroll: false } : null);
  } catch (error) {
    console.warn('[size-chart] could not load chart', handle, error);

    // Fail visibly, not as a dead control: a card trigger is a link, so follow it to
    // the product page; the PDP trigger is already there and only marks itself.
    if (cta.href) {
      window.location.assign(cta.href);
      return;
    }
    cta.setAttribute('aria-disabled', 'true');
  } finally {
    cta.removeAttribute('aria-busy');
  }
});
